import { useEffect, useRef, useState } from 'react'

import { computeFitTransform } from '../canvasHelpers'

export const INITIAL_ZOOM = 0.85
export const ZOOM_MIN = 0.35
export const ZOOM_MAX = 2

// Owns the canvas viewport: zoom + pan state, the Figma-style wheel handler
// (cmd/ctrl = zoom around the cursor, shift = horizontal pan, else 2D pan),
// the empty-surface drag-to-pan, and the zoom/fit controls. The drag hook
// reads `zoomRef` to convert screen deltas to world space.
export const useCanvasPanZoom = ({ canvasElementRef }) => {
  const [zoom, setZoom] = useState(INITIAL_ZOOM)
  const [pan, setPan] = useState({ x: 0, y: 0 })

  // Anchor for an in-progress empty-surface pan (set on mousedown, cleared on
  // pointerup). Refs mirror the latest zoom/pan so the wheel handler reads them
  // without re-binding listeners on every state change.
  const panRef = useRef(null)
  const zoomRef = useRef(zoom)
  const currentPanRef = useRef(pan)

  useEffect(() => {
    zoomRef.current = zoom
  }, [zoom])

  useEffect(() => {
    currentPanRef.current = pan
  }, [pan])

  // Window listener for the empty-surface pan gesture. Acts only while panRef
  // is set; node dragging uses its own listener (in useNodeDrag) and never
  // sets panRef, so the two gestures don't collide.
  useEffect(() => {
    const handlePointerMove = (event) => {
      if (panRef.current) {
        setPan({
          x: event.clientX - panRef.current.x,
          y: event.clientY - panRef.current.y,
        })
      }
    }
    const handlePointerUp = () => {
      panRef.current = null
    }
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [])

  const handleCanvasMouseDown = (event) => {
    // Only pan when clicking the empty surface (not a node, controls, or hint).
    if (event.target.closest('.voice-agents__canvas-node')) {
      return
    }
    if (event.target.closest('.voice-agents__canvas-controls')) {
      return
    }
    if (event.target.closest('.voice-agents__canvas-hint')) {
      return
    }
    panRef.current = { x: event.clientX - pan.x, y: event.clientY - pan.y }
  }

  // Figma-style wheel: cmd/ctrl = zoom around the cursor; shift = horizontal
  // pan; otherwise 2D pan. Trackpad pinch fires wheel with ctrlKey, so it
  // routes to the zoom branch automatically.
  const handleWheel = (event) => {
    const isZoom = event.ctrlKey || event.metaKey
    if (!isZoom && event.deltaX === 0 && event.deltaY === 0) {
      return
    }
    event.preventDefault()

    if (isZoom) {
      const canvasEl = canvasElementRef.current
      if (!canvasEl) {
        return
      }
      const rect = canvasEl.getBoundingClientRect()
      const cursorX = event.clientX - rect.left
      const cursorY = event.clientY - rect.top
      const oldZoom = zoomRef.current
      const oldPan = currentPanRef.current
      // Trackpad pinch deltas are tiny (≈1); mouse wheels emit ~100. Scale
      // by deltaY directly so both feel proportional.
      const factor = Math.exp(-event.deltaY * 0.01)
      const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, oldZoom * factor))
      if (newZoom === oldZoom) {
        return
      }
      const worldX = (cursorX - oldPan.x) / oldZoom
      const worldY = (cursorY - oldPan.y) / oldZoom
      setZoom(newZoom)
      setPan({
        x: cursorX - worldX * newZoom,
        y: cursorY - worldY * newZoom,
      })
      return
    }

    // Pan. Shift+wheel maps vertical scroll to horizontal — matches Figma so
    // mouse-wheel users can still pan sideways.
    const dx = event.shiftKey ? event.deltaY : event.deltaX
    const dy = event.shiftKey ? 0 : event.deltaY
    setPan((prev) => ({ x: prev.x - dx, y: prev.y - dy }))
  }

  const handleZoomIn = () => setZoom((prev) => Math.min(ZOOM_MAX, prev + 0.1))
  const handleZoomOut = () => setZoom((prev) => Math.max(ZOOM_MIN, prev - 0.1))
  const handleResetZoom = () => setZoom(1)

  // Fit + center the given positions inside the canvas viewport. No-op when the
  // canvas can't be measured (test env / not yet laid out) or there's nothing
  // to fit, leaving the current view untouched.
  const fitTo = (positions) => {
    const canvasEl = canvasElementRef.current
    if (!canvasEl) {
      return
    }
    const fit = computeFitTransform(positions, canvasEl.clientWidth, canvasEl.clientHeight, {
      minZoom: ZOOM_MIN,
      maxZoom: 1,
    })
    if (fit) {
      setZoom(fit.zoom)
      setPan(fit.pan)
    }
  }

  const resetView = () => {
    setZoom(INITIAL_ZOOM)
    setPan({ x: 0, y: 0 })
  }

  return {
    zoom,
    pan,
    zoomRef,
    handleWheel,
    handleCanvasMouseDown,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    fitTo,
    resetView,
  }
}
